import React, { type ReactNode, useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  Chip,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  Label,
  Menu,
  ModalIsland,
  P,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../shared/utils/localization';
import {
  activateKey,
  addKey,
  deleteKey,
  getKeys,
  type SSOKey,
} from '../data-sources';

import './SSOKeySettings.css';

const MAX_CUSTOM_KEYS = 10;
const DEFAULT_KID = 'rsa-generated';
const PK_PATTERN =
  /^-----BEGIN PRIVATE KEY-----\n.*\n-----END PRIVATE KEY-----\n?$/s;
const CERT_PATTERN =
  /^-----BEGIN CERTIFICATE-----\n.*\n-----END CERTIFICATE-----\n?$/s;
const NAME_PATTERN = /^[a-zA-Z0-9_]{1,32}$/;

enum MenuOptions {
  download_key = 'download_key',
  activate_key = 'activate_key',
  remove_key = 'remove_key',
}

/** Configuration of SSO keys */
export function SSOKeySettings(): ReactNode {
  const l = useInvertedLocalize();
  const [isPending, setIsPending] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [hasDeleteError, setHasDeleteError] = useState(false);
  const [hasActivateError, setHasActivateError] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [hasAddError, setHasAddError] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [cert, setCert] = useState('');
  const [deleteRequestedFor, setDeleteRequestedFor] = useState<string | null>(
    null
  );
  const [keys, setKeys] = useState<SSOKey[]>([]);
  const [certWasBlurred, setCertWasBlurred] = useState(false);
  const [privKeyWasBlurred, setPrivKeyWasBlurred] = useState(false);
  const certError = certWasBlurred && !CERT_PATTERN.test(cert);
  const privKeyError = privKeyWasBlurred && !PK_PATTERN.test(privateKey);

  const handleDownloadClick = (key: SSOKey) => {
    const blob = new Blob(
      [
        `-----BEGIN CERTIFICATE-----
${key.x5c}
-----END CERTIFICATE-----`,
      ],
      { type: 'application/x-x509-cert' }
    );
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');

    a.href = url;
    a.download = `${key.kid}.pem`;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getKeyNameError = () => {
    if (!NAME_PATTERN.test(keyName)) {
      return l(
        'Key name can be up to 32 alphaneumeric and underscore characters only'
      );
    }

    if (keys.filter((k) => k.name === keyName).length > 0) {
      return l('Key name must be unique');
    }

    return '';
  };

  const keyNameError = getKeyNameError();

  const handleAddClick = () => {
    setKeyName('');
    setIsAdding(true);
  };

  const handleDeleteClick = (kid: string) => {
    setHasDeleteError(false);
    setDeleteRequestedFor(kid);
  };

  const handleActivateClick = (kid: string) => {
    setHasActivateError(false);
    activateKey(kid)
      .then(() => {
        loadKeys();
      })
      .catch(() => {
        setHasActivateError(true);
      });
  };

  const handleDeleteModalClose = () => {
    setDeleteRequestedFor(null);
  };

  const handleDeleteConfirm = () => {
    setIsPending(true);

    deleteKey(deleteRequestedFor!) // eslint-disable-line @typescript-eslint/no-non-null-assertion -- can't invoke this handler unless this state is set
      .then(() => {
        loadKeys();
      })
      .catch(() => {
        setHasDeleteError(true);
      })
      .finally(() => {
        setIsPending(false);
        setDeleteRequestedFor(null);
      });
  };

  const handleSaveClick = () => {
    const certMatch = CERT_PATTERN.test(cert);
    const privKeyMatch = PK_PATTERN.test(privateKey);

    setCertWasBlurred(!certMatch);
    setPrivKeyWasBlurred(!privKeyMatch);

    if (!certMatch || !privKeyMatch) {
      return;
    }

    setIsPending(true);
    setHasAddError(false);

    addKey(keyName, privateKey, cert)
      .then(() => {
        loadKeys();
        setPrivateKey('');
        setCert('');
        setIsAdding(false);
      })
      .catch(() => {
        setHasAddError(true);
      })
      .finally(() => {
        setIsPending(false);
      });
  };

  const canSave =
    !keyNameError && CERT_PATTERN.test(cert) && PK_PATTERN.test(privateKey);

  const loadKeys = () => {
    setIsPending(true);
    getKeys()
      .then((keyData) => {
        setKeys(keyData);
        setIsPending(false);
      })
      .catch(() => {
        setHasLoadError(true);
      });
  };

  useEffect(() => {
    loadKeys();
  }, []);

  useEffect(() => {
    if (!certError && certWasBlurred) {
      setCertWasBlurred(false);
    }
  }, [certError, certWasBlurred]);

  useEffect(() => {
    if (!privKeyError && privKeyWasBlurred) {
      setPrivKeyWasBlurred(false);
    }
  }, [privKeyError, privKeyWasBlurred]);

  const renderKey = (
    key: SSOKey,
    index: number,
    allKeys: SSOKey[]
  ): ReactNode => {
    const isDefault = key.kid === DEFAULT_KID;
    const isActive = index === allKeys.length - 1;

    const options = [
      {
        label: l('Download'),
        value: MenuOptions.download_key,
      },
    ];

    if (!isActive) {
      options.push({
        label: l('Activate'),
        value: MenuOptions.activate_key,
      });

      if (!isDefault) {
        options.push({
          label: l('Remove'),
          value: MenuOptions.remove_key,
        });
      }
    }

    const handleMenu = ({ value }: { value: MenuOptions }) => {
      switch (value) {
        case MenuOptions.download_key:
          handleDownloadClick(key);
          break;
        case MenuOptions.activate_key:
          handleActivateClick(key.kid);
          break;
        case MenuOptions.remove_key:
          handleDeleteClick(key.kid);
          break;
      }
    };

    return (
      <Box className='Auth_SSOKeySettings_key' key={key.kid}>
        <P className='Auth_SSOKeySettings_key_id' variant={2}>
          {isDefault ? l('Default Key') : key.name}
        </P>
        {isActive && (
          <Chip
            kind='subtleFilled'
            color='success'
            label={l('Active')}
            size='small'
            isInteractionDisabled
          />
        )}
        <Menu
          options={options}
          onChange={handleMenu}
          trigger={
            <ButtonIcon
              ariaLabel={l('Options')}
              kind='ghost'
              icon={<Icon kind='MoreVertical' size='small' />}
            />
          }
        />
      </Box>
    );
  };

  return (
    <Box className='Auth_SSOKeySettings'>
      <Button
        kind='outlined'
        leftIcon={<Icon kind='Add' />}
        className='Auth_SSOKeySettings_addKey'
        onClick={() => handleAddClick()}
        isDisabled={keys.length >= MAX_CUSTOM_KEYS + 1 /* default key */}>
        {l('Add Key')}
      </Button>
      <section className='Auth_SSOKeySettings_section'>
        {hasActivateError && (
          <InlineMessage
            colorTheme='critical'
            text={l(
              'An error occurred while activating the key. Please try again.'
            )}
          />
        )}
        {hasDeleteError && (
          <InlineMessage
            colorTheme='critical'
            text={l(
              'An error occurred while deleting the key. Please try again.'
            )}
          />
        )}
        {keys.map(renderKey)}
        {hasLoadError && (
          <InlineMessage
            colorTheme='critical'
            text={l(
              'An error occurred while loading your SSO keys. Please refresh this page to try again.'
            )}
          />
        )}
      </section>
      <ModalIsland
        title={l('Add Key')}
        subtitle={l('Add a new RSA 256 key')}
        isOpen={isAdding}
        onClose={() => setIsAdding(false)}
        FooterActionBarComp={
          <FooterActionBar
            size='large'
            className='Auth_SSOListView_modalFooter'>
            <Button
              onClick={handleSaveClick}
              isDisabled={isPending || !canSave}
              className='Auth_SSOKeySettings_add'>
              {l('Save')}
            </Button>
          </FooterActionBar>
        }
        contentBody={
          <section className='Auth_SSOKeySettings_addKeyModalContent'>
            <InputField
              name='Auth_SSOKeySettings_keyName'
              placeholder=''
              label={
                <Label kind='body2-bold' htmlFor='Auth_SSOKeySettings_keyName'>
                  {l('Key Name')}
                </Label>
              }
              value={keyName}
              setValue={setKeyName}
              isRequired
              isCritical={!!keyName && !!keyNameError}
              CriticalHelperText={
                <InlineMessage
                  text={keyNameError}
                  colorTheme='critical'
                  size='small'
                  leftIcon={<Icon kind='AlertCriticalFilled' />}
                />
              }
            />
            <InputField
              placeholder={`-----BEGIN PRIVATE KEY-----
ABCDEFG...
-----END PRIVATE KEY-----`}
              kind='multiline'
              name='Auth_SSOKeySettings_privateKey'
              value={privateKey}
              setValue={setPrivateKey}
              onBlur={() => setPrivKeyWasBlurred(true)}
              isCritical={privKeyError}
              isRequired
              CriticalHelperText={
                <InlineMessage
                  text={l('Private key does not match expected format')}
                  colorTheme='critical'
                  size='small'
                  leftIcon={<Icon kind='AlertCriticalFilled' />}
                />
              }
              label={
                <Label
                  kind='body2-bold'
                  htmlFor='Auth_SSOKeySettings_privateKey'>
                  {l('Private Key')}
                </Label>
              }
            />

            <InputField
              placeholder={`-----BEGIN CERTIFICATE-----
ABCDEFG...
-----END CERTIFICATE-----`}
              kind='multiline'
              name='Auth_SSOKeySettings_cert'
              value={cert}
              setValue={setCert}
              onBlur={() => setCertWasBlurred(true)}
              isCritical={certError}
              isRequired
              CriticalHelperText={
                <InlineMessage
                  text={l('Certificate does not match expected format')}
                  colorTheme='critical'
                  size='small'
                  leftIcon={<Icon kind='AlertCriticalFilled' />}
                />
              }
              label={
                <Label kind='body2-bold' htmlFor='Auth_SSOKeySettings_cert'>
                  {l('Certificate')}
                </Label>
              }
            />
            {hasAddError && (
              <InlineMessage
                colorTheme='critical'
                text={l(
                  'An error occurred while adding the key. Please try again.'
                )}
              />
            )}
          </section>
        }
      />
      <ModalIsland
        kind='critical'
        alertTitle={l('Confirm Removal')}
        alertSubtitle={l(
          'Removing this key will terminate any current logins signed by this key. Are you sure you want to remove this key?'
        )}
        isOpen={deleteRequestedFor !== null}
        onClose={handleDeleteModalClose}
        primaryButton={
          <Button
            type='button'
            kind='outlined'
            isEnabled
            size='medium'
            onClick={handleDeleteConfirm}>
            {l('Terminate logins and remove key')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            size='medium'
            onClick={handleDeleteModalClose}>
            {l('Cancel')}
          </Button>
        }
      />
    </Box>
  );
}
